import type { FC } from "react";

// Third Party
import {
  createFileRoute,
  useNavigate,
  type NavigateOptions,
} from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useAccessPermission } from "@/helpers/acessPermission";

// UI
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import Pagination from "@/components/Pagination";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";

import {
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
} from "@/components/ui/card";

import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbList,
  BreadcrumbPage,
} from "@/components/ui/breadcrumb";

// Services and Utils
import { getDirector } from "@/services/directors";
import { aws } from "@/helpers/aws";

//icon
import { Eye } from "lucide-react";
import NotPermission from "@/components/NotPermission";
import { clearLocalStorage } from "@/helpers/accessLocalStorage";
import { ErrorResponse } from "@/types/ErrorResponse";
import type { AxiosError } from "axios";

interface DirectorItem {
  id: string;
  firstName: string;
  lastName: string;
  profileUrl: string;
  gender: string;
  phone: string;
  position: string;
  agreementDate: string;
}

interface DirectorResponse {
  status: boolean;
  message: string;
  total: number;
  data: DirectorItem[];
}

interface EditProps {
  name: string;
  photo: string;
}
interface EditPropsname {
  name: string;

  gender: string;
}
export const Route = createFileRoute("/_layout-private/report/director/")({
  component: RouteComponent,
  validateSearch: (search: Record<string, unknown>) => {
    return {
      page: Number(search.page) || 1,
    };
  },
});
function RouteComponent() {
  const { page } = Route.useSearch();
  const navigate = useNavigate();
  const companyId = JSON.parse(localStorage.getItem("companyId") || "null");
  const { director_Evaluation_Report } = useAccessPermission();

  const {
    data: boardsData,
    isLoading,
    error,
  } = useQuery<DirectorResponse, AxiosError<ErrorResponse>>({
    queryKey: ["boards", companyId],
    queryFn: () => getDirector({ companyId, skip: (page - 1) * 40, limit: 40 }),
  });

  if (error) {
    if (error?.response?.data?.message === "LOGGED_OUT") {
      console.log("clearing storage due to logged out");
      clearLocalStorage();
      window.location.reload();
    }
  }

  // Functions
  const handlePageClick = (page: number) => {
    navigate({
      search: (prev) => ({
        ...prev,
        page,
      }),
    } as NavigateOptions);
  };

  const Profile: FC<EditProps> = ({ name, photo }) => {
    return (
      <Avatar>
        <AvatarImage
          src={aws + photo}
          alt={name}
          className="object-cover w-10 h-10 rounded-full"
        />
        <AvatarFallback>
          <span className="text-xl font-semibold text-gray-500">
            {name.charAt(0)}
          </span>
        </AvatarFallback>
      </Avatar>
    );
  };

  const Profilename: FC<EditPropsname> = ({ name, gender }) => {
    return (
      <div className="flex gap-x-2 items-center">
        <Eye size={16} color="#007AFF" />
        <span className="ml-2 text-sm font-medium">
          {gender === "MALE" ? "ທ່ານ " : "ທ່ານ ນາງ "}
          {name}
        </span>
      </div>
    );
  };
  const translateGender = (gender: string) => {
    return gender === "MALE" ? "ຊາຍ" : "ຍິງ";
  };
  return director_Evaluation_Report.isRead ? (
    <section>
      <Breadcrumb>
        <BreadcrumbList>
          <BreadcrumbItem>
            <BreadcrumbPage>ລາຍງານການປະເມີນອຳນວຍການ</BreadcrumbPage>
          </BreadcrumbItem>
        </BreadcrumbList>
      </Breadcrumb>

      <Card className="mt-8">
        <CardHeader>
          <CardTitle>ລາຍງານການປະເມີນອຳນວຍການ</CardTitle>
          <CardDescription>ລາຍການລາຍງານການປະເມີນອຳນວຍການ</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto w-full rounded-sm border">
            <div>
              <Table className="mb-4">
                <TableHeader>
                  <TableRow>
                    <TableHead>ລໍາດັບ</TableHead>
                    <TableHead>ຮູບໂປຣຟາຍ</TableHead>
                    <TableHead>ຊື່ ແລະ ນາມສະກຸນ</TableHead>
                    {/* <TableHead>ບ່ອນສັງກັດ</TableHead> */}

                    <TableHead>ເພດ</TableHead>
                    <TableHead>ເບີໂທ</TableHead>
                    <TableHead>ຕຳແໜ່ງ</TableHead>
                    <TableHead>ວັນທີແຕ່ງຕັ້ງ</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {isLoading ? (
                    <TableRow>
                      <TableCell colSpan={7} className="text-center">
                        Loading...
                      </TableCell>
                    </TableRow>
                  ) : (
                    boardsData?.data.map(
                      (board: DirectorItem, index: number) => (
                        <TableRow
                          key={board.id}
                          onClick={() => {
                            try {
                              localStorage.setItem("directorId", board.id);
                              navigate({
                                to: "/report/director/details",
                                search: { active: "yourself" },
                              });
                            } catch (error) {
                              console.error("Error saving board ID:", error);
                            }
                          }}
                          className="cursor-pointer"
                        >
                          <TableCell className="font-medium">
                            {index + 1}
                          </TableCell>
                          <TableCell className="font-medium">
                            <Profile
                              name={`${board.firstName} ${board.lastName}`}
                              photo={`${board.profileUrl}`}
                            />{" "}
                          </TableCell>
                          <TableCell className="font-medium">
                            <Profilename
                              name={`${board.firstName} ${board.lastName}`}
                              gender={`${board.gender}`}
                            />{" "}
                          </TableCell>
                          {/* <TableCell>{board.company.companyName}</TableCell> */}

                          <TableCell>{translateGender(board.gender)}</TableCell>
                          <TableCell>{board.phone}</TableCell>
                          <TableCell>{board.position}</TableCell>
                          <TableCell>
                            {new Date(board.agreementDate).toLocaleDateString()}
                          </TableCell>
                        </TableRow>
                      )
                    )
                  )}
                </TableBody>
              </Table>
            </div>
          </div>
          <Pagination
            handlePageClick={handlePageClick}
            totalItems={boardsData?.total || 0}
            itemsPerPage={40}
            currentPage={page}
          />
        </CardContent>
      </Card>
    </section>
  ) : (
    <NotPermission />
  );
}
